import { useDebounceFn } from '@vueuse/core'
import { ref, watch } from 'vue'

/**
 *
 * The Generic type TResult is the type of the search results.
 * The apiCall function is a function that makes the API call to get the search results.
 * The inputValue is the value of the search input.
 * The signal is the signal to abort the search.
 * The search results are returned as an array of TResult.
 * The search results are returned as an array of SearchResult<Stakeholder>.
 * The search results are returned as an array of SearchResult<Stakeholder>.
 *
 * Usage example:
 * ```ts
 * const search = useDebouncedSearchInput<SearchResult<Stakeholder>>({
 *   apiCall: (inputValue, signal) => stakeholdersSearchRequest({
 *     query: `${inputValue}`,
 *     queryMode: 'free-text'
 *   }, {
 *     signal
 *   })
 * })
 * ```
 */
export function useDebouncedSearchInput<TResult>(params: {
  apiCall: (
    inputValue: string | undefined | null,
    signal?: AbortSignal
  ) => Promise<TResult[]>
}) {
  const activeAbortController = ref<AbortController | undefined>(undefined)
  const pendingSearchValue = ref<string | undefined | null>(undefined)

  const searchResults = ref<TResult[]>([])

  const isSearching = ref(false)

  const searchInputValue = ref<string>('')

  async function callSearch(
    inputValue: string | undefined | null,
    signal?: AbortSignal
  ) {
    if (!inputValue?.trim()) {
      return
    }

    try {
      const response = await params.apiCall(inputValue, signal)
      searchResults.value = response
    } catch (error) {
      if ((error as { code?: string } | null)?.code === 'ERR_CANCELED') {
        return
      }
      throw error
    } finally {
      isSearching.value = false
    }
  }

  const debouncedCallSearch = useDebounceFn(
    (value: string | undefined | null) => {
      // Check if the search value has changed since this was scheduled
      // If it has, don't execute (a new debounced call will handle it)
      if (pendingSearchValue.value !== value) {
        return
      }
      const abortController = new AbortController()
      activeAbortController.value = abortController
      callSearch(value, abortController.signal)
    },
    500
  )

  function abortActiveSearch() {
    if (activeAbortController.value) {
      activeAbortController.value.abort()
      activeAbortController.value = undefined
      isSearching.value = false
    }
  }

  function clearSearchResults() {
    abortActiveSearch()
    searchResults.value = []
  }

  function clearAll() {
    clearSearchResults()
    searchInputValue.value = ''
    pendingSearchValue.value = undefined
    activeAbortController.value = undefined
  }

  watch(searchInputValue, async (value) => {
    isSearching.value = false
    abortActiveSearch()
    // Cancel any pending debounced call
    if (
      'cancel' in debouncedCallSearch &&
      typeof debouncedCallSearch.cancel === 'function'
    ) {
      debouncedCallSearch.cancel()
    }
    // Update the pending search value
    pendingSearchValue.value = value
    if (value?.trim()) {
      isSearching.value = true
      debouncedCallSearch(value)
    } else {
      clearSearchResults()
    }
  })

  return {
    searchInputValue,
    searchResults,
    isSearching,
    debouncedCallSearch,
    callSearch,
    abortActiveSearch,
    clearSearchResults,
    clearAll
  }
}
